/*
 * StreamBank Admin v38.0
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types'

import {Conditional} from "../Conditional";

import * as renderers from './ModelRenderers';
import {
    useDebouncedEffect,
} from "../../Hooks/EffectHooks";
import {    
    useFieldDefinitionVisibility,
    useGenerateGuid,
    useValidations
} from "../../Hooks/GenericModelHooks";
import {ObjectUtils} from "../../Utils";

export const GenericModelViewer = (props) => {

    const {
        Association: association,
        CustomData: customData,
        Data: data = {},
        DefaultSelectedFieldDefKey,
        EditMode: editMode,
        HideGroups: hiddenGroupKeys = [],
        HideFields: hiddenFieldKeys = [],
        IsNewModel,
        Model: model,
        OnValidationUpdate: onValidationUpdate,
        RootData: rootData,
        SkipBorder,
        Update: onUpdate
    } = props;
    
    const fieldDefinitionVisibility = useFieldDefinitionVisibility(props);
    
    const [validations, setValidations, isValid] = useValidations();
    const [childValidations, setChildValidations, isChildValid] = useValidations();
    const generateGuid = useGenerateGuid();
    const [editorGuid, setEditorGuid] = useState(generateGuid());

    const fieldDefinitionGroups = model?.getFieldDefGroups?.() ?? [];
    // hides groups with hiddenGroupKeys
    // and fields based on permissions, etc
    const visibleFieldDefinitionGroups = fieldDefinitionVisibility(fieldDefinitionGroups, hiddenGroupKeys, hiddenFieldKeys);

    useEffect(() => {
        //set default values for FieldDefs.
        let updates = [];

        for(const fieldDefinitionGroup of fieldDefinitionGroups) {
            for(const fieldDef of fieldDefinitionGroup.value) {
                if (fieldDef.defaultValue) { // if there is a default value
                    let value = fieldDef.getter(data); //get the current

                    if (value == null) { // if it's blank
                        updates.push({ // set to the default.
                            def: fieldDef,
                            value: fieldDef.defaultValue
                        });
                    }
                }
            }
        }
        
        updateFields(updates);

        // now handled by useDebouncedEffect
        // this.validate();
        
        return () => {
            onValidationUpdate({}, editorGuid);
        }
        
    }, []);

    const updateFields = (values) => {
        if(values.length) {
            let target = {...data};
            
            for(const {def, value} of values) {
                def?.setter?.(target, value);
            }

            onUpdate?.(target);
        }
    }

    const updateField = (def, value) => {
        updateFields([{def, value}]);
    }
    
    // validate
    useDebouncedEffect(async () => {
        let ret = {};

        for(const fieldGroup of visibleFieldDefinitionGroups) {
            
            for (const field of fieldGroup.value) {

                const {associationName, getter, key, model: associationModel, validators} = field;
                if (association) {
                    if (association.using) {
                        if (key === association.using) {
                            return;
                        }
                    }
                }

                ret[key] = {
                    valid: true,
                    message: ""
                };

                if (validators && validators.length) {
                    validators.forEach((validator) => {
                        const value = getter(data);
                        let modelToValidate = model;
                        let dataToValidate = data;

                        // send appropriate model and data to child model validators
                        if (associationName && associationModel) {
                            modelToValidate = associationModel;
                            dataToValidate = dataToValidate[associationName]
                        }

                        if (validator?.show(editMode)) {
                            if (!validator?.logic(value, modelToValidate, dataToValidate, customData)) {
                                ret[key] = {
                                    valid: false,
                                    message: validator?.message(modelToValidate)
                                }
                            }
                        }
                    });
                }
            }
        }

        if(model.recordValidators) {
            for (const validator of model.recordValidators) {
                if (validator.show(editMode)) {
                    const valid = await validator.logic(model, data);
                    if(!valid) {
                        ret[null] = {
                            valid: false,
                            message: validator.message(model, data)
                        };
                    }
                }
            }
        }
        
        setValidations(ret);
    }, [model, ObjectUtils.SafeStringify(visibleFieldDefinitionGroups), ObjectUtils.SafeStringify(data)], 300);

    useEffect(() => {
        onValidationUpdate?.(
            {...validations, ...childValidations},
            editorGuid,
        );
    }, [ObjectUtils.SafeStringify(validations), ObjectUtils.SafeStringify(childValidations)]);
    
    const onChildValidation = (newChildValidations, editorGuid) => {
        let isValid = true;

        for (let key in newChildValidations) {
            if(!newChildValidations[key].valid) {
                isValid = false;
                break;
            }
        }

        // TODO: I'm not sure if we need this if statement anymore?
        if(!childValidations[editorGuid] || isValid != childValidations[editorGuid].valid) {
            setChildValidations({
                ...childValidations,
                [editorGuid]: {valid: isValid},
            });
        }
    }

    const getOptionsForGroup = (fieldGroup) => {
        return {
            renderer: model.groupRenderers?.[fieldGroup.key] ?? 'StandardModelViewerRenderer'
        }
    }

    const renderFieldGroup = (fieldGroup) => {
        let groupOptions = getOptionsForGroup(fieldGroup);
        const RendererComponent = renderers?.[groupOptions.renderer];

        return   <Conditional key={fieldGroup.key} Condition={(fieldGroup.value ?? []).length > 0}>
                    <Conditional Condition={fieldGroup.key && fieldGroup.groupTitle !== '' && fieldGroup.groupTitle != null} key="header">
                        <h3 className="table-headers">{fieldGroup.groupTitle}</h3>
                    </Conditional>
                    <RendererComponent
                        key="body"
                        FieldGroup={fieldGroup}
                        SkipBorder={SkipBorder}
                        EditMode={editMode}
                        RootData={rootData || data}
                        Data={data}
                        Validation={validations}
                        UpdateFields={updateFields}
                        UpdateField={updateField}
                        FieldDefinitionGroups={fieldDefinitionGroups}
                        GetFieldDefs={(includeHidden) => includeHidden ? fieldDefinitionGroups : visibleFieldDefinitionGroups}
                        OnValidationUpdate={onChildValidation}
                        CustomData={customData}
                        IsNewModel={IsNewModel}
                        DefaultSelectedFieldDefKey={DefaultSelectedFieldDefKey}
                    />
                </Conditional>;
    }

    let classNames = []

    if(typeof model?.classNamesFromData == "function"){
        classNames = classNames.concat(model?.classNamesFromData(data) ?? []);
    }

    return   <div className={classNames.join(' ')} onSubmit={e => e.preventDefault()}>
                    <Conditional Condition={validations[null]}>
                        <div className="sb-model-viewer-validation-failure-text">
                            {validations[null]?.message}
                        </div>
                    </Conditional>
                    {
                        visibleFieldDefinitionGroups.map((fieldGroup) => 
                            renderFieldGroup(fieldGroup)
                        )
                    }
            </div>;

}


GenericModelViewer.propTypes = {
  Association: PropTypes.object,
  CustomData: PropTypes.object,
  Data: PropTypes.object,
  DefaultSelectedFieldDefKey: PropTypes.string,
  EditMode: PropTypes.bool,
  HasPermission: PropTypes.any,
  HideGroups: PropTypes.any,
  IsNewModel: PropTypes.bool,
  Model: PropTypes.any.isRequired,
  OnValidationUpdate: PropTypes.func,
  RootData: PropTypes.object,
  SkipBorder: PropTypes.bool,
  Update: PropTypes.func
}

GenericModelViewer.defaultProps = {
  EditMode: false,
  IsNewModel: false,
  SkipBorder: false
}
